import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Plot from "react-plotly.js";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./style/SentimentAnalysis.module.css";
import commonStyles from "../../../styles/Common.module.css";
import { useSentimentData } from "./hook/useSentimentData";
import { getFilteredTickers } from "../../../utils/tickerValidator";

// [추가] Toast 임포트 (VaR.js와 동일) adsfadsfasdfasd
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // 스타일이 없다면 필요할 수 있음

// --- 1. Constants & Helpers (외부로 분리) --- 
const COMMON_LAYOUT = {
    paper_bgcolor: 'rgba(0,0,0,0)', // adsfasd
    plot_bgcolor: 'rgba(0,0,0,0)',
    font: { family: 'Inter, sans-serif', color: '#64748B' },
    margin: { l: 40, r: 20, t: 40, b: 40 },
    showlegend: false,
    hovermode: 'x unified',
    xaxis: { showgrid: false, zeroline: false, fixedrange: true },
    yaxis: { showgrid: true, gridcolor: '#F1F5F9', zeroline: false, fixedrange: true }
};

const getSource = (url) => {
    try { return new URL(url).hostname.replace('www.', '').split('.')[0].toUpperCase(); }
    catch { return "SOURCE"; }
};

const getBadge = (polarity) => {
    if (!polarity) return { l: 'NEU', c: styles.neuBadge };
    if (polarity > 0.1) return { l: 'POS', c: styles.posBadge };
    if (polarity < -0.1) return { l: 'NEG', c: styles.negBadge };
    return { l: 'NEU', c: styles.neuBadge };
};

// --- 2. Sub-Components (UI 분리) ---

// AI 요약 컴포넌트
const AiSummary = ({ data, activeTarget, onAssetClick, t }) => {
    if (!data?.sentiments?.length) return null;
    
    const last = data.sentiments.at(-1);
    const info = last > 0.1 ? { label: t('sentiment.positive'), color: "#10B981", momentum: t('sentiment.upwardMomentum') }
               : last < -0.1 ? { label: t('sentiment.negative'), color: "#EF4444", momentum: t('sentiment.marketCaution') }
               : { label: t('sentiment.neutral'), color: "#64748B", momentum: "" };

    const topics = data.networkTag?.nodes?.filter(n => n.type !== 'center').slice(0, 3).map(n => n.label).join(", ") || "";
    const stocks = data.networkSymbol?.nodes?.filter(n => n.type !== 'center').slice(0, 3).map(n => n.label) || [];
    const targetLabel = activeTarget.type === 'ticker' ? activeTarget.value : `#${activeTarget.value}`;

    return (
        <div className={styles.aiSummaryContent}>
            <div className={styles.summaryStatus} dangerouslySetInnerHTML={{ __html: t('sentiment.aiSummaryStatus', { target: targetLabel, color: info.color, label: info.label }) }} />
            <div className={styles.summaryDetail}>
                <span dangerouslySetInnerHTML={{ __html: t('sentiment.aiSummaryDetail', { topics }) }} /> {info.momentum}
            </div>
            {stocks.length > 0 && (
                <div className={styles.relatedAssetsContainer}>
                    <span className={styles.relatedAssetsLabel}>{t('sentiment.relatedAssets')}</span>
                    <div className={styles.stockTagsWrapper}>
                        {stocks.map((s, i) =>
                            <span 
                                key={i} 
                                className={styles.stockTag} 
                                onClick={(e) => { 
                                    e.stopPropagation(); 
                                    // 기존 로직 유지하되 type 명시
                                    onAssetClick(s, 'symbol'); 
                                }}>
                                {s.split('.')[0]}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// 검색바 컴포넌트
const SearchSection = ({ mode, setMode, inputs, setInputs, onSearch, suggestions, focusedIndex, setFocusedIndex, setSuggestions, t }) => {
    const wrapperRef = useRef(null);
    useEffect(() => {
        const clickOut = (e) => wrapperRef.current && !wrapperRef.current.contains(e.target) && setSuggestions([]);
        document.addEventListener("mousedown", clickOut); return () => document.removeEventListener("mousedown", clickOut);
    }, [setSuggestions]);

    return (
        <section className={styles.controlBar}>
            <div className={styles.searchWrapper}>
                <div className={styles.toggleContainer}>
                    {['ticker', 'tag'].map(m => (
                        <button 
                            key={m} 
                            className={mode === m ? styles.activeToggle : styles.toggleBtn} 
                            onClick={() => { 
                                setMode(m); 
                                setInputs({ ticker: '' }); 
                                }}>
                            {m === 'ticker' ? t('sentiment.ticker') : t('sentiment.topic')}
                        </button>
                    ))}
                </div>
                <div className={styles.searchForm} ref={wrapperRef}>
                    <input
                        className={styles.tickerInput}
                        placeholder={mode === 'ticker' ? t('sentiment.searchTickerPlaceholder') : t('sentiment.searchTopicPlaceholder')}
                        value={inputs.ticker}
                        autoComplete="off"
                        onChange={(e) => {
                            setInputs({ ticker: e.target.value });
                            setFocusedIndex(-1);
                            if (mode === 'ticker') setSuggestions(getFilteredTickers(e.target.value.toUpperCase()));
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); onSearch(focusedIndex >= 0 ? suggestions[focusedIndex]?.symbol : inputs.ticker); }
                            else if (e.key === 'Escape') setSuggestions([]);
                            else if (e.key === 'ArrowDown') setFocusedIndex(p => Math.min(p + 1, suggestions.length - 1));
                            else if (e.key === 'ArrowUp') setFocusedIndex(p => Math.max(p - 1, -1));
                        }}
                    />
                    {suggestions.length > 0 && mode === 'ticker' && (
                        <ul className={styles.suggestionList}>
                            {suggestions.map((item, idx) => (
                                <li key={idx} className={`${styles.suggestionItem} ${idx === focusedIndex ? styles.focusedItem : ''}`} onClick={() => onSearch(item.symbol)}>
                                    <strong>{item.symbol}</strong><span className={styles.subText}>{item.name}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button onClick={() => onSearch()} className={styles.searchButton}>{t('sentiment.analyze')}</button>
                </div>
            </div>
        </section>
    );
};

// 뉴스 리스트 컴포넌트
const NewsSection = ({ newsList, selectedDate, onDateReset, onScrollDateChange, t }) => {
    const scrollRef = useRef(null);
    const lastScrollTime = useRef(0);
    
    const handleScroll = useCallback(() => {
        if (selectedDate || !scrollRef.current || Date.now() - lastScrollTime.current < 100) return;
        lastScrollTime.current = Date.now();
        const items = Array.from(scrollRef.current.children[0].children);
        const viewTop = scrollRef.current.getBoundingClientRect().top;
        const visibleItem = items.find(item => {
            const rect = item.getBoundingClientRect();
            return rect.top >= viewTop - 50 && rect.top <= viewTop + 150;
        });
        if (visibleItem?.dataset?.date) onScrollDateChange(visibleItem.dataset.date);
    }, [selectedDate, onScrollDateChange]);

    return (
        <section className={`${styles.card} ${styles.newsCard}`}>
            <div className={styles.cardHeader}>
                <h3>{selectedDate ? t('sentiment.newsOnDate', { date: selectedDate }) : t('sentiment.realTimeFeed')}</h3>
                <div className={styles.newsHeaderActions}>
                    {selectedDate && <button onClick={onDateReset} className={styles.resetBtn}>{t('sentiment.resetFilter')}</button>}
                    <span className={styles.newsCountText}>{t('sentiment.newsCount', { count: newsList.length })}</span>
                </div>
            </div>
            <div className={styles.scrollContainer} ref={scrollRef} onScroll={handleScroll}>
                <div className={styles.newsList}>
                    {newsList.map((news, i) => {
                        // const b = getBadge(news.sentiment);
                        const b = getBadge(news.sentiment?.polarity);
                        return (
                            <div key={i} className={styles.newsItem} data-date={news.date.split('T')[0]}>
                                <div className={styles.newsMeta}>
                                    <div className={styles.newsMetaLeft}><span className={b.c}>{b.l}</span><span className={styles.newsSource}>{getSource(news.link)}</span></div>
                                    <span className={styles.newsTimestamp}>{new Date(news.date).toLocaleDateString()}</span>
                                </div>
                                <a href={news.link} target="_blank" rel="noreferrer" className={styles.newsTitle}>{news.title}</a>
                                <div className={styles.tagContainer}>{news.tags?.slice(0, 4).map((tag, idx) => <span key={idx} className={styles.newsTag}>#{tag}</span>)}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

// 스켈레톤 로더
const DashboardSkeleton = () => (
    <div className={styles.fadeIn}>
        <div className={styles.summaryHeader}><div className={styles.skeletonCircle} /><div className={styles.skeletonSummaryBody} /></div>
        <div className={styles.mainDashboard}>{[1, 2].map(i => <div key={i} className={styles.chartCard}><div className={styles.skeletonBox} /></div>)}</div>
    </div>
);

// --- 3. Custom Hook (Chart Logic) ---
const useChartData = (data, uiState, networkMode, t) => {
    return useMemo(() => {
        if (!data) return { sentiment: {}, network: {} };
        const { sentiments = [], dates = [], networkSymbol, networkTag } = data;
        const activeDate = uiState.selectedDate || uiState.currentScrollDate;
        const activeNet = networkMode === 'symbol' ? networkSymbol : networkTag;

        return {
            sentiment: {
                data: dates.length ? [{ x: dates, y: sentiments, type: 'scatter', mode: 'lines', fill: 'tozeroy', fillcolor: 'rgba(99, 102, 241, 0.1)', line: { color: '#6366F1', width: 3, shape: 'spline' } }] : [],
                layout: { ...COMMON_LAYOUT, title: { text: t('sentiment.sentimentTrend'), font: { size: 14, color: '#0F172A' } }, shapes: activeDate ? [{ type: 'line', x0: activeDate, x1: activeDate, y0: 0, y1: 1, yref: 'paper', line: { color: '#0F172A', dash: 'dot' } }] : [] }
            },
            network: {
                data: activeNet?.nodes ? [
                    // Edges
                    { x: activeNet.edges?.flatMap(e => [activeNet.nodes.find(n=>n.id===e.source)?.x, activeNet.nodes.find(n=>n.id===e.target)?.x, null]), y: activeNet.edges?.flatMap(e => [activeNet.nodes.find(n=>n.id===e.source)?.y, activeNet.nodes.find(n=>n.id===e.target)?.y, null]), mode: 'lines', line: { color: '#E2E8F0', width: 1 }, type: 'scatter', hoverinfo: 'none' },
                    // Nodes
                    { 
                        x: activeNet.nodes.map(n => n.x), 
                        y: activeNet.nodes.map(n => n.y), 
                        mode: 'markers+text', 
                        text: activeNet.nodes.map(n => n.type==='center'?'':n.label), 
                        textposition: 'bottom center', 
                        marker: { size: activeNet.nodes.map(n => n.type==='center'?20:n.size*1.5), color: activeNet.nodes.map(n => n.type==='center'?'#6366F1':(networkMode==='symbol'?'#10B981':'#94A3B8')) }, 
                        type: 'scatter',
                        // [NEW] 클릭 이벤트에서 원본 노드 데이터를 식별하기 위해 customdata 추가
                        customdata: activeNet.nodes 
                    }
                ] : [],
                layout: { 
                  ...COMMON_LAYOUT, 
                  //title: { text: networkMode === 'symbol' ? t('sentiment.relatedStocks') : t('sentiment.keyTopics') }, 
                  xaxis: { showgrid: false, showticklabels: false }, yaxis: { showgrid: false, showticklabels: false } 
                }
            }
        };
    }, [data, uiState, networkMode, t]);
};

// --- Main Component ---
function SentimentAnalysis() {
    const { t } = useTranslation();
    const { data, isLoading, error, loadData } = useSentimentData();
    
    // [추가] Router Hooks
    const location = useLocation();
    const navigate = useNavigate();

    // [변경] 초기 상태를 URL 파라미터에서 가져오도록 수정
    const getInitialState = () => {
        const params = new URLSearchParams(location.search);
        const q = params.get('q');
        const type = params.get('type');
        
        if (q && type) {
            return { value: q, type: type };
        }
        return { value: "inflation", type: "tag" }; // 기본값
    };

    const initialState = getInitialState();

    // State
    // [변경] inputs 초기값도 URL 상태에 맞게 설정
    const [inputs, setInputs] = useState({ 
        ticker: initialState.type === 'ticker' ? initialState.value.split('.')[0] : initialState.value 
    });
    const [focusedIndex, setFocusedIndex] = useState(-1);
    
    // [변경] activeTarget 초기값 설정
    const [activeTarget, setActiveTarget] = useState(initialState);
    
    // [변경] searchMode 초기값 설정
    const [searchMode, setSearchMode] = useState(initialState.type === 'ticker' ? "ticker" : "tag");
    
    const [networkMode, setNetworkMode] = useState("tag");
    const [activeTab, setActiveTab] = useState("trend");
    const [uiState, setUiState] = useState({ selectedDate: null, currentScrollDate: null });
    const [suggestions, setSuggestions] = useState([]);

    // [추가] activeTarget이 변경될 때 URL 업데이트
    useEffect(() => {
        const params = new URLSearchParams();
        params.set('q', activeTarget.value);
        params.set('type', activeTarget.type);
        
        // 현재 URL과 다를 경우에만 replace (무한 루프 방지 및 히스토리 관리)
        const currentParams = new URLSearchParams(location.search);
        if (currentParams.get('q') !== activeTarget.value || currentParams.get('type') !== activeTarget.type) {
            navigate(`?${params.toString()}`, { replace: true });
        }
    }, [activeTarget, navigate, location.search]);

    // Hooks & Effects
    useEffect(() => { loadData(activeTarget.value, activeTarget.type); }, [activeTarget, loadData]);
    // [추가] 차트 데이터 생성 시 t 함수 의존성 확인 (기존 코드에 t가 포함되어 있었으므로 유지)
    const charts = useChartData(data, uiState, networkMode, t); 

    // Handlers
    // const handleSearch = (val = inputs.ticker) => {
    //     if (!val) return;
    //     const target = val.trim().toUpperCase();
    //     if (searchMode === 'ticker') setActiveTarget({ value: target.includes('.') ? target : `${target}.US`, type: 'ticker' });
    //     else setActiveTarget({ value: target.toLowerCase(), type: 'tag' });
    //     setSuggestions([]);
    // };
    const handleSearch = (val = inputs.ticker) => {
        if (!val) return;
        
        const target = val.trim().toUpperCase();
        
        // [추가] 검색창에 입력된 텍스트도 선택한 값으로 업데이트
        // 티커 모드일 경우 대문자로, 태그 모드일 경우 소문자로 표시되게 처리합니다.
        const displayValue = searchMode === 'ticker' ? target : target.toLowerCase();
        setInputs({ ticker: displayValue });

        // 실제 분석 대상(Target) 설정
        if (searchMode === 'ticker') {
            setActiveTarget({ 
                value: target.includes('.') ? target : `${target}.US`, 
                type: 'ticker' 
            });
        } else {
            setActiveTarget({ 
                value: target.toLowerCase(), 
                type: 'tag' 
            });
        }
        
        setSuggestions([]); // 추천 목록 닫기
    };

    const handleNodeClick = (value, type) => {
        if (!value || type === 'center') return; 

        // 1. 모드 결정
        const newMode = type === 'symbol' ? 'ticker' : 'tag';
        
        // 2. 검색어 정제
        let cleanValue = value;
        if (newMode === 'ticker') {
            cleanValue = value.toUpperCase().split('.')[0];
        } else {
            cleanValue = value.toLowerCase(); 
        }

        // 3. UI 상태 업데이트
        setSearchMode(newMode);       
        setInputs({ ticker: cleanValue }); 
        setSuggestions([]);           

        // 4. 실제 데이터 로드 트리거 (이 부분이 useEffect를 통해 URL 업데이트도 트리거함)
        const targetValue = newMode === 'ticker' 
            ? (cleanValue.includes('.') ? cleanValue : `${cleanValue}.US`) 
            : cleanValue;
            
        setActiveTarget({ value: targetValue, type: newMode });
    };
    
    // [추가] 공유 버튼 핸들러
    const handleShareLink = () => {
        navigator.clipboard.writeText(window.location.href);
        toast.info(t("linkCopied") || "링크가 클립보드에 복사되었습니다.");
    };

    const displayedNews = useMemo(() => {
        if (!data?.allNews) return [];
        return uiState.selectedDate ? data.allNews.filter(n => n.date.startsWith(uiState.selectedDate)) : data.allNews;
    }, [data, uiState.selectedDate]);

    // useChartData 함수 등은 컴포넌트 외부에 정의되어 있다고 가정 (원본 코드 유지)

    return (
        <div className={styles.pageContainer}>
            <main className={commonStyles.Container}>
                <div className={commonStyles.introContainer}>
                    <p className={commonStyles.introMain}>{t('sentiment.title')}</p>
                    <p className={commonStyles.introSub}>{t('sentiment.subTitle')}</p>
                </div>

                <SearchSection 
                    mode={searchMode} setMode={setSearchMode} 
                    inputs={inputs} setInputs={setInputs} 
                    onSearch={handleSearch} t={t}
                    suggestions={suggestions} setSuggestions={setSuggestions}
                    focusedIndex={focusedIndex} setFocusedIndex={setFocusedIndex}
                />
                
         

                {error && <div className={styles.errorMsg}>⚠️ {error}</div>}
                {isLoading && <DashboardSkeleton />}
                
                {!isLoading && !error && data && (
                    <div className={styles.fadeIn}>
                        <section className={styles.summaryHeader}>
                            <div style={{ fontSize: '1.5rem', marginRight: '16px', opacity: 0.8 }}>💡</div>
                            <AiSummary 
                                data={data} 
                                activeTarget={activeTarget} 
                                onAssetClick={handleNodeClick} 
                                t={t} 
                            />
                        </section>

                        {/* ... (기존 차트 및 뉴스 섹션 유지) ... */}
                        <div className={styles.mobileTabContainer}>
                            {['trend', 'network'].map(tab => (
                                <button key={tab} className={`${styles.mobileTabBtn} ${activeTab === tab ? styles.mobileTabActive : ''}`} onClick={() => setActiveTab(tab)}>
                                    {tab === 'trend' ? t('sentiment.sentimentTrend') : t('sentiment.relationshipMap')}
                                </button>
                            ))}
                        </div>

                        <section className={styles.mainDashboard}>
                            <div className={`${styles.chartCard} ${activeTab !== 'trend' ? styles.mobileHidden : ''}`}>
                                <Plot data={charts.sentiment.data} layout={charts.sentiment.layout} useResizeHandler className={styles.chartContainer} config={{ displayModeBar: false }} 
                                      onClick={e => e.points && setUiState({ ...uiState, selectedDate: e.points[0].x, currentScrollDate: null })} />
                            </div>
                            <div className={`${styles.networkCard} ${activeTab !== 'network' ? styles.mobileHidden : ''}`}>
                                <div className={styles.networkControls}>
                                    {['symbol', 'tag'].map(m => (
                                        <button key={m} onClick={() => setNetworkMode(m)} className={networkMode === m ? styles.netBtnActive : styles.netBtn}>
                                            {m === 'symbol' ? t('sentiment.stocks') : t('sentiment.topics')}
                                        </button>
                                    ))}
                                </div>
                                <Plot 
                                    data={charts.network.data} 
                                    layout={charts.network.layout} 
                                    useResizeHandler 
                                    className={styles.chartContainer} 
                                    config={{ displayModeBar: false }}
                                    onClick={(e) => {
                                        if (e.points && e.points[0]) {
                                            const pointIndex = e.points[0].pointIndex;
                                            const nodeData = e.points[0].data.customdata[pointIndex];
                                            if (nodeData) {
                                                handleNodeClick(nodeData.label, nodeData.type);
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </section>

                        <NewsSection 
                            newsList={displayedNews} 
                            selectedDate={uiState.selectedDate} 
                            onDateReset={() => setUiState({ ...uiState, selectedDate: null })}
                            onScrollDateChange={(d) => setUiState(p => ({ ...p, currentScrollDate: d }))} 
                            t={t}
                        />
                    </div>
                )}
            </main>
                   {/* [추가] 공유 버튼 (결과가 로드되면 표시) */}
                {!isLoading && !error && data && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                        <button
                            onClick={handleShareLink}
                            className={styles.shareButton} // 위에서 추가한 클래스명
                        >
                            <span role="img" aria-label="share">🔗</span> {t("shareLink", "결과 공유하기")}
                        </button>

                    </div>
                )}
            
            {/* [추가] Toast Container (VaR.js와 동일 설정) */}
            <ToastContainer
                position="bottom-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />  
        </div>
    );
}

export default SentimentAnalysis;